import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CATEGORY_LABELS = {
  academic: 'Academic',
  privilege: 'Privilege',
  experience: 'Experience',
  general: 'General',
};

const RewardTable = ({ rewards, onEdit, onDelete, onToggleActive }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = rewards?.filter(r =>
    r?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    r?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-border overflow-hidden">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Rewards Catalog ({rewards?.length})</h3>
        <div className="relative">
          <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search rewards..."
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            className="pl-8 pr-3 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary w-48"
          />
        </div>
      </div>
      {filtered?.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-3">🎁</div>
          <p className="text-muted-foreground text-sm">
            {rewards?.length === 0 ? 'No rewards created yet. Add your first reward!' : 'No rewards match your search.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Reward</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Star Cost</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered?.map(reward => (
                  <tr key={reward?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Icon name={reward?.icon || 'Gift'} size={18} className="text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-sm">{reward?.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{reward?.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {CATEGORY_LABELS?.[reward?.category] || reward?.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <span className="text-base">⭐</span>
                        <span className="font-semibold text-amber-600">{reward?.starCost}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onToggleActive(reward?.id, !reward?.isActive)}
                        className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          reward?.isActive
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full ${reward?.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <span>{reward?.isActive ? 'Active' : 'Inactive'}</span>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => onEdit(reward)}
                          className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Icon name="Pencil" size={15} />
                        </button>
                        <button
                          onClick={() => onDelete(reward?.id)}
                          className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-border">
            {filtered?.map(reward => (
              <div key={reward?.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Icon name={reward?.icon || 'Gift'} size={20} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{reward?.title}</p>
                      <div className="flex items-center space-x-2 mt-0.5">
                        <span className="text-sm">⭐</span>
                        <span className="text-sm font-medium text-amber-600">{reward?.starCost}</span>
                        <span className="text-xs text-muted-foreground">• {CATEGORY_LABELS?.[reward?.category]}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button onClick={() => onEdit(reward)} className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg">
                      <Icon name="Pencil" size={15} />
                    </button>
                    <button onClick={() => onDelete(reward?.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-lg">
                      <Icon name="Trash2" size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RewardTable;
